import { isGraphqlConfigured, graphqlRequest } from './graphqlClient';
import { auth } from '../config/firebaseConfig';

export { isGraphqlConfigured as isEntitlementApiConfigured };

// `source` is `'stripe' | 'apple' | 'both' | 'none'` from the backend (plus the
// local_override/error sentinels the FE injects). Kept as a string union for
// forward-compat with any future source the backend adds.
export type EntitlementSource = 'stripe' | 'apple' | 'both' | 'none' | string;

export interface Entitlement {
  isPro: boolean;
  source: EntitlementSource;
  status: string;
  plan?: string | null;
  expiresAt?: string | null;
  // Apple StoreKit product id (pro_monthly_ios / pro_yearly_ios) when source includes Apple.
  appleProductId?: string | null;
}

const fetchMyEntitlement = async (): Promise<Entitlement> => {
  const data = await graphqlRequest<{ entitlement: Entitlement }>(
    `query Entitlement { entitlement { isPro source status plan expiresAt appleProductId } }`,
  );
  return data.entitlement;
};

// ── Single-flight + short memo ────────────────────────────────────────────────
// Every mounted useEntitlement (3–9 instances across the shell) refetches on the
// same trigger (purchase.updated, app refresh, focus), so one event used to fan
// out into that many identical network requests. Same pattern as useLocation's
// native single-flight: share one in-flight promise, and memo the settled result
// just long enough (~2s) to collapse the burst — well under any window in which
// the entitlement could legitimately change again. Failures are NOT memoized:
// every awaiter of the shared promise sees the rejection, and the next call
// gets a fresh attempt.
//
// Both are keyed by the signed-in UID at call time: after an account switch,
// account A's in-flight request or memo must never answer for account B.
const ENTITLEMENT_MEMO_MS = 2_000;
let entitlementInFlight: { uid: string; promise: Promise<Entitlement> } | null = null;
let entitlementMemo: { uid: string; value: Entitlement; at: number } | null = null;

export const getMyEntitlement = (): Promise<Entitlement> => {
  const uid = auth.currentUser?.uid ?? '';
  if (entitlementMemo && entitlementMemo.uid === uid && Date.now() - entitlementMemo.at < ENTITLEMENT_MEMO_MS) {
    return Promise.resolve(entitlementMemo.value);
  }
  if (entitlementInFlight?.uid !== uid) {
    const flight = {
      uid,
      promise: fetchMyEntitlement()
        .then((value) => {
          entitlementMemo = { uid, value, at: Date.now() };
          return value;
        })
        .finally(() => {
          if (entitlementInFlight === flight) entitlementInFlight = null;
        }),
    };
    entitlementInFlight = flight;
  }
  return entitlementInFlight.promise;
};

/** Test-only: clear the single-flight/memo module state between test cases. */
export const __resetEntitlementSingleFlightForTests = (): void => {
  entitlementInFlight = null;
  entitlementMemo = null;
};

const entitlementApi = { isEntitlementApiConfigured: isGraphqlConfigured, getMyEntitlement };
export default entitlementApi;
